/**
 * vatReserve — reine Zustandsmaschine der Cockpit-Kachel „USt-Rücklage" (M-CFO-2).
 *
 * Bildet Steuerstatus (§19-Kleinunternehmer, Versteuerungsart) + die bereits per
 * `buildVatReturn` berechnete UStVA-Zahllast des laufenden Quartals fail-closed auf
 * einen anzeige-fertigen View-Zustand ab. KEINE Firebase-/React-Imports — nur der
 * `VatReturnFigures`-Typ (compile-time erased). Trivial unit-testbar.
 *
 * FACHLICH/RECHTLICH SENSIBEL — die Gate-Kaskade setzt bindende Jurist-Auflagen um
 * (`jurist-ustrücklage-wortlaute.md`, `architecture-cockpit-cfo.md` §8.2/§9):
 *
 *   Auswertungsreihenfolge (normativ, §8.2):
 *     G2  ustvaEnabled === false                       → hidden (Engine M8 selbst noch nicht frei)
 *     §19 isSmallBusiness === true  (VOR taxationMode!) →
 *           reverseChargeOwedCents > 0 (smallBusinessHasReverseChargeDuty)
 *                                                       → smallBusinessReverseCharge (§13b-Hinweis)
 *           sonst                                       → hidden  (NIEMALS „keine USt"-Positivaussage)
 *     G4  taxationMode fehlt/unbekannt                  → configure (CTA, KEINE Zahl)
 *     G5  taxationMode === 'ist'                        → istPending (Hinweis, KEINE Zahl)
 *     G6  taxationMode === 'soll' + figures            → reserve/refund/zero (einziger Betragspfad)
 *
 * BINDENDE Auflage 1 (BLOCK bei Verstoß): Für `isSmallBusiness === true` entsteht
 * NIE ein Betrag und NIE eine positive „keine USt"/„0 €"-Aussage — nur `hidden`
 * (kein Text) oder der neutrale `smallBusinessReverseCharge`-Hinweis. Der §19-Ast
 * wird deshalb VOR jeder taxationMode-/Betragslogik ausgewertet.
 *
 * @module utils/vatReserve
 */

import type { VatReturnFigures } from './vatReturn';

/**
 * Anzeige-Zustand der USt-Rücklage-Kachel. Diskriminierte Union — der Tile-Renderer
 * schaltet allein über `kind`, sodass jeder Zustand exakt einen freigegebenen
 * Jurist-Wortlaut trägt (kein „durchrutschender" Betrag im §19-Fall).
 */
export type VatReserveView =
  /** G2 / normaler §19-KU (kein §13b): Kachel wird gar nicht gerendert — KEIN Text. */
  | { kind: 'hidden' }
  /** §19-KU MIT §13b-/Erwerb-Pflicht: neutraler §13b-Hinweis, KEIN Betrag (Report §4). */
  | { kind: 'smallBusinessReverseCharge' }
  /** taxationMode fehlt: CTA „Versteuerungsart festlegen" → Firmenprofil, KEINE Zahl. */
  | { kind: 'configure' }
  /** Ist-Versteuerung (§20 UStG): neutraler Hinweis, KEINE Zahl (Ist-Pfad nicht gebaut). */
  | { kind: 'istPending' }
  /** kz83 > 0: Betrags-Empfehlung „Leg rund … zurück" + Countdown + Warn-Transparenz. */
  | {
      kind: 'reserve';
      /** Roh-Zahllast in Integer-Cents (>0). Anzeige rundet der Tile konservativ auf. */
      amountCents: number;
      /** Nächste UStVA-Frist (ISO) oder null bei kaputtem Referenzdatum. */
      deadlineISO: string | null;
      /** Ganzzahlige Tage bis zur Frist oder null. */
      daysRemaining: number | null;
      /** `unclassifiedIncome.count` — >0 ⇒ Warn-Chip „noch nicht zuordenbar". */
      unclassifiedCount: number;
      /** `excludedDraftVatOwedCount` — >0 ⇒ Warn-Chip „Entwürfe nicht berücksichtigt". */
      excludedDraftCount: number;
    }
  /** kz83 < 0: „Voraussichtlich Erstattung" (kein „leg −X zurück"). */
  | { kind: 'refund'; amountCents: number }
  /** kz83 === 0: KONDITIONALE Aussage „nach bisher erfassten Daten … noch keine Zahllast". */
  | { kind: 'zero' };

/** Eingabe von {@link deriveVatReserveView}. */
export interface DeriveVatReserveViewInput {
  /** `REACT_APP_USTVA_ENABLED` — false ⇒ Kachel bleibt aus (G2). */
  ustvaEnabled: boolean;
  /** `sellerData.isSmallBusiness` (§19). undefined = nicht gesetzt (⇒ Nicht-KU-Pfad). */
  isSmallBusiness: boolean | undefined;
  /** `sellerData.taxationMode`. undefined ⇒ configure (KEINE Zahl). */
  taxationMode: 'soll' | 'ist' | undefined;
  /**
   * `buildVatReturn(...)`-Ergebnis des lfd. Quartals. null solange nicht geladen.
   * Trägt im §19-Ast das `smallBusinessHasReverseChargeDuty`-Signal, im Soll-Ast die
   * `kz83PayableCents`-Zahllast + die Transparenz-Buckets.
   */
  figures: VatReturnFigures | null;
  /** Ergebnis von `computeNextUstvaDeadline(today)` — nur im reserve-Zustand angezeigt. */
  deadline: { deadlineISO: string | null; daysRemaining: number | null };
}

/**
 * Leitet den fail-closed Anzeige-Zustand der USt-Rücklage-Kachel ab (§8.2).
 *
 * TOTAL (wirft nie): jede Eingabe-Kombination liefert genau einen {@link VatReserveView}.
 * Die §19-Verzweigung steht VOR der taxationMode-Auswertung (Auflage 1) — ein
 * Kleinunternehmer erhält niemals einen Betrag oder eine „keine USt"-Positivaussage.
 *
 * @param input - Steuerstatus + geladene Kennzahlen + Frist.
 * @returns Diskriminierter View-Zustand für den Tile-Renderer.
 */
export function deriveVatReserveView(input: DeriveVatReserveViewInput): VatReserveView {
  const { ustvaEnabled, isSmallBusiness, taxationMode, figures, deadline } = input;

  // G2 — die UStVA-Engine (M8) ist selbst noch nicht freigegeben (OP-1/OP-4).
  if (!ustvaEnabled) return { kind: 'hidden' };

  // §19-KU (Auflage 1: VOR taxationMode/Betragslogik). NIE ein Betrag, NIE „keine USt".
  if (isSmallBusiness === true) {
    if (figures?.smallBusinessHasReverseChargeDuty === true) {
      return { kind: 'smallBusinessReverseCharge' };
    }
    return { kind: 'hidden' };
  }

  // G5 — Ist-Versteuerung: Soll-Wert wäre falsch, daher neutraler Hinweis ohne Zahl.
  if (taxationMode === 'ist') return { kind: 'istPending' };

  // G6 — Soll-Versteuerung: einziger Betragspfad. Ohne geladene figures (defensiv,
  // der Tile ruft hier nur im ready-Zustand mit non-null figures) fail-closed hidden.
  if (taxationMode === 'soll') {
    if (figures === null) return { kind: 'hidden' };
    const kz83 = figures.kz83PayableCents;
    if (kz83 > 0) {
      return {
        kind: 'reserve',
        amountCents: kz83,
        deadlineISO: deadline.deadlineISO,
        daysRemaining: deadline.daysRemaining,
        unclassifiedCount: figures.unclassifiedIncome.count,
        excludedDraftCount: figures.excludedDraftVatOwedCount,
      };
    }
    if (kz83 < 0) return { kind: 'refund', amountCents: Math.abs(kz83) };
    return { kind: 'zero' };
  }

  // G4 — taxationMode fehlt (undefined) ODER unbekannter String (defensiv): KEINE Zahl.
  return { kind: 'configure' };
}
